
import { useState, useEffect } from "react";
import { AdvancedMarker, InfoWindow, useAdvancedMarkerRef } from "@vis.gl/react-google-maps";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bed, Bath, Maximize2, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { useClusterer } from "@/contexts/ClustererContext";
import { useBuyer } from "@/contexts/BuyerContext";

interface Property {
  id: string;
  address: string;
  city: string;
  state: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  image_url: string | null;
  property_type: string;
  status: string;
}

interface PropertyMapMarkerProps {
  property: Property;
  position: { lat: number; lng: number };
}

// Custom gold pin marker component
const GoldPinMarker = () => (
  <div className="relative cursor-pointer">
    <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.2 0 0 7.2 0 16C0 24 16 40 16 40S32 24 32 16C32 7.2 24.8 0 16 0Z" fill="#ffd700"/>
      <circle cx="16" cy="16" r="8" fill="#080c14"/>
      <text x="16" y="20" textAnchor="middle" fill="#ffd700" fontSize="8" fontWeight="bold">$</text>
    </svg>
  </div>
);

const PropertyMapMarker = ({ property, position }: PropertyMapMarkerProps) => {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [isOpen, setIsOpen] = useState(false);
  const { clusterer } = useClusterer();
  const { isBuyer } = useBuyer();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Register marker with clusterer
  useEffect(() => {
    if (marker && clusterer) {
      clusterer.addMarker(marker);
      return () => {
        clusterer.removeMarker(marker);
      };
    }
  }, [marker, clusterer]);

  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={position}
        onClick={() => setIsOpen(true)}
        title={`${property.address} - ${formatCurrency(property.price)}`}
      >
        <GoldPinMarker />
      </AdvancedMarker>
      
      {isOpen && marker && (
        <InfoWindow
          anchor={marker}
          onCloseClick={() => setIsOpen(false)}
          maxWidth={320}
        >
          <Card className="border-0 shadow-none m-0 p-0">
            <CardContent className="p-3">
              <div className="space-y-3">
                {/* Property Image */}
                {property.image_url && (
                  <div className="w-full h-32 overflow-hidden rounded-md">
                    <img
                      src={property.image_url}
                      alt={property.address}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                {/* Property Details */}
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-sm leading-tight">
                        {property.address}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {property.city}, {property.state}
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {property.property_type}
                    </Badge>
                  </div>
                  
                  <p className="text-lg font-bold text-[#ffd700] mb-2">
                    {formatCurrency(property.price)}
                  </p>
                  
                  {/* Property Stats */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                    <div className="flex items-center gap-1">
                      <Bed className="h-3 w-3" />
                      <span>{property.bedrooms}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Bath className="h-3 w-3" />
                      <span>{property.bathrooms}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Maximize2 className="h-3 w-3" />
                      <span>{property.square_feet.toLocaleString()} sq ft</span>
                    </div>
                  </div>
                  
                  {/* View Details Button */}
                  <Link to={isBuyer ? `/buyer/properties/${property.id}` : `/seller/properties/${property.id}`}>
                    <Button size="sm" className="w-full bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200]">
                      <Eye className="h-3 w-3 mr-2" />
                      View Details
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </InfoWindow>
      )}
    </>
  );
};

export default PropertyMapMarker;
